import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
// import { useTranslation } from 'react-i18next';
import TempleIcon from '../../common/assets/logos/tdb.svg';
import AyyappanIcon from '../../common/assets/logos/Ayyappan.svg';
import { useClock } from '../../common/utils/clock';
import Footer from './Footer';
import VanchiStatusInactiveIcon from '../../common/assets/icons/ic_vanchiStatus_inactive.svg';
import VanchiStatusActiveIcon from '../../common/assets/icons/ic_vanchiStatus_active.svg';
import DownloadInactiveIcon from '../../common/assets/icons/ic_download_inactive.svg';
import DownloadActiveIcon from '../../common/assets/icons/ic_download_active.svg';
import UserInactiveIcon from '../../common/assets/icons/ic_user_inactive.svg';
import UserActiveIcon from '../../common/assets/icons/ic_user_active.svg';
import ScooproIcon from '../../common/assets/icons/Scoopro_Logo.svg';

interface MasterLayoutProps {
  onLogout: () => void;
  children: React.ReactNode;
}

const MasterLayout: React.FC<MasterLayoutProps> = ({
  onLogout: _onLogout,
  children,
}) => {
  const clockData = useClock();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // const toggleLanguage = () => {
  //   const newLang = i18n.language === 'en' ? 'ml' : 'en';
  //   i18n.changeLanguage(newLang);
  // };


  // const filteredNavItems = navItems.filter(item =>
  //   !item.roles || hasRole(item.roles)
  // );


  // Helper function to determine if a route is active (base route only)
  const isActiveRoute = (path: string) => {
    const currentBaseRoute = location.pathname.split('/')[1];
    const targetBaseRoute = path.split('/')[1];
    return currentBaseRoute === targetBaseRoute;
  }

  // State to track which dropdowns are open
  const [openDropdowns, setOpenDropdowns] = useState<Set<string>>(new Set());
  
  // State for navbar dropdown
  const [isNavbarDropdownOpen, setIsNavbarDropdownOpen] = useState(false);

  // Helper function to determine if a dropdown should be open by default based on its routes
  const isDropdownActiveByRoute = (routes: string[]) => {
    return routes.some(route => isActiveRoute(route));
  }

  // Navigation configuration for dynamic dropdown handling
  const navigationConfig = {
    kanikkaVanchi: {
      routes: ['/vanchistatus', '/report', '/users'],
      dropdownId: 'kanikkaSubmenu'
    },
    masterRecords: {
      routes: ['/AddUser', '/AddNada', '/AddVanchiName', '/AddVanchiNo'],
      dropdownId: 'MasterRecSubMenu'
    }
  };

  // Initialize dropdown states based on routes on mount and route changes
  useEffect(() => {
    const initialOpenDropdowns = new Set<string>();
    
    Object.entries(navigationConfig).forEach(([, config]) => {
      if (isDropdownActiveByRoute(config.routes)) {
        initialOpenDropdowns.add(config.dropdownId);
      }
    });
    
    setOpenDropdowns(initialOpenDropdowns);
  }, [location.pathname]);

  // Handle navbar dropdown toggle
  const handleNavbarDropdownToggle = () => {
    setIsNavbarDropdownOpen(!isNavbarDropdownOpen);
  };

  // Close navbar dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsNavbarDropdownOpen(false);
      }
    };

    if (isNavbarDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNavbarDropdownOpen]);

  // Handle dropdown toggle
  const handleDropdownToggle = (dropdownId: string) => {
    setOpenDropdowns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dropdownId)) {
        newSet.delete(dropdownId); // Close if open
      } else {
        newSet.add(dropdownId); // Open if closed
      }
      return newSet;
    });
  };

  // Helper function to get dropdown attributes with toggle functionality
  const getDropdownAttributes = (sectionKey: keyof typeof navigationConfig) => {
    const config = navigationConfig[sectionKey];
    const isOpen = openDropdowns.has(config.dropdownId);
    
    return {
      triggerAttributes: {
        'aria-expanded': isOpen,
        'aria-controls': config.dropdownId,
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          handleDropdownToggle(config.dropdownId);
        }
      },
      contentAttributes: {
        className: `sidebar-subMenu ${isOpen ? 'show' : ''}`,
        id: config.dropdownId,
        style: {
          display: isOpen ? 'block' : 'none'
        }
      }
    };
  };

  return (
    <div className='master-Layout d-flex flex-column min-vh-100'>
      <nav className="navbar sticky-top sabariMala-navbar px-3">
        <div className='d-flex justify-content-between w-100 h-100'>
          <div className='d-flex align-items-center gap-3'>
            <TempleIcon width="60" height="60" />
            <div className='d-flex flex-column text-primary'>
              <h6 className='navbar-templeName mb-1'>SWAMY SARANAM</h6>
              <h4 className='fw-medium navbar-templeBoard mb-1'>TRAVANCORE DEVASWOM BOARD</h4>
              <h4 className='fw-light navbar-templeDeva mb-0'>SABARIMALA DEVASWOM</h4>
            </div>
          </div>
          <div className='d-flex flex-row align-items-center text-primary masterLayout-time'>
            <div className='me-3'>{clockData.time} {clockData.timeLabel}</div>
            <div className='text-end'>{clockData.date}</div>
          </div>
          <div className='d-flex flex-row align-items-center gap-3'>
            <div className="dropdown" ref={dropdownRef}>
              <button
                className="btn btn-link d-flex align-items-center text-primary text-decoration-none dropdown-toggle"
                type="button"
                onClick={handleNavbarDropdownToggle}
                aria-expanded={isNavbarDropdownOpen}
              >
                <span className='navbar-logoutBtn text-white'>BN</span>
              </button>
              <ul className={`dropdown-menu text-small shadow dropdown-menu-end ${isNavbarDropdownOpen ? 'show' : ''}`}>
                <li>
                  <div className='navbar-language'>
                    <input type="radio" className="btn-check" name="options-base" id="en" autoComplete="off" />
                    <label className="btn" htmlFor="en">EN</label>

                    <input type="radio" className="btn-check" name="options-base" id="ml" autoComplete="off" />
                    <label className="btn" htmlFor="ml">ML</label>
                  </div>
                </li>
                <li><button className="dropdown-item text-primary" onClick={_onLogout}>LogOut</button></li>
              </ul>
            </div>
            <AyyappanIcon width="60" height="60" />
          </div>
        </div>
      </nav>
      <div className="d-flex flex-grow-1"> {/* Main content area takes available space */}
        {/* Sidebar */}
        <div
          className="sabariMala-sidebar position-fixed d-flex flex-column"
          style={{
            width: 'var(--sidebar-width)',
            transition: 'width 0.3s ease'
          }}
        >
          <div className='d-flex flex-column sidebar-mainMenuScroll'>
            <a
              href="#kanikkaSubmenu"
              className="text-primary text-decoration-none border-bottom border-primary sidebar-mainMenu"
              role="button"
              {...getDropdownAttributes('kanikkaVanchi').triggerAttributes}
            >
              Kanikka Vanchi
            </a>
            {/* Submenu with collapse */}
            <div {...getDropdownAttributes('kanikkaVanchi').contentAttributes}>
              <ul className="list-unstyled py-2 mb-0">
                <li className={`w-100 ${isActiveRoute('/vanchistatus') ? 'active' : ''}`}>
                  <Link to="/vanchistatus" className="d-flex text-primary text-decoration-none w-100 px-3 py-2 gap-2">
                    {isActiveRoute('/vanchistatus') ? (
                      <VanchiStatusActiveIcon width={18} height={18} />
                    ) : (
                      <VanchiStatusInactiveIcon width={18} height={18} />
                    )}
                    Vanchi Status
                  </Link>
                </li>
                <li className={`w-100 ${isActiveRoute('/report') ? 'active' : ''}`}>
                  <Link to="/report" className="d-flex text-primary text-decoration-none w-100 px-3 py-2 gap-2">
                    {isActiveRoute('/report') ? (
                      <DownloadActiveIcon width={18} height={18} />
                    ) : (
                      <DownloadInactiveIcon width={18} height={18} />
                    )}
                    Download Report
                  </Link>
                </li>
                <li className={`w-100 ${isActiveRoute('/users') ? 'active' : ''}`}>
                  <Link to="/users" className="d-flex text-primary text-decoration-none w-100 px-3 py-2 gap-2">
                    {isActiveRoute('/users') ? (
                      <UserActiveIcon width={18} height={18} />
                    ) : (
                      <UserInactiveIcon width={18} height={18} />
                    )}
                    Users
                  </Link>
                </li>
              </ul>
            </div>
            <a
              href="javascript:void(0)"
              className="text-primary text-decoration-none border-bottom border-primary sidebar-mainMenu"
              onClick={(e) => e.preventDefault()}
              style={{ cursor: 'default' }}
            >
              Staff Management
            </a>

            {/* Submenu with collapse */}
            <div className="collapse sidebar-subMenu" id="StaffMgmtSubmenu">
              <ul className="list-unstyled py-2 mb-0">
                <li className="w-100 active">
                  <a href="javascript:void(0)" className="d-flex text-primary text-decoration-none w-100 px-3 py-2 gap-2">
                    <VanchiStatusInactiveIcon width={18} height={18} />
                    Vanchi Status 1
                  </a>
                </li>
                <li className="w-100">
                  <a href="javascript:void(0)" className="d-flex text-primary text-decoration-none w-100 px-3 py-2 gap-2">
                    <DownloadInactiveIcon width={18} height={18} />
                    Download Report 1
                  </a>
                </li>
                <li className="w-100">
                  <a href="javascript:void(0)" className="d-flex text-primary text-decoration-none w-100 px-3 py-2 gap-2">
                    <UserInactiveIcon width={18} height={18} />
                    Users 1
                  </a>
                </li>
              </ul>
            </div>  
            <a
              href="javascript:void(0)"
              className="text-primary text-decoration-none border-bottom border-primary sidebar-mainMenu"
              onClick={(e) => e.preventDefault()}
              style={{ cursor: 'default' }}
            >
              Guest House
            </a>

            {/* Submenu with collapse */}
            <div className="collapse sidebar-subMenu" id="GuestHouseSubMenu">
              <ul className="list-unstyled py-2 mb-0">
                <li className="w-100 active">
                  <a href="javascript:void(0)" className="d-flex text-primary text-decoration-none w-100 px-3 py-2 gap-2">
                    <VanchiStatusInactiveIcon width={18} height={18} />
                    Vanchi Status 1
                  </a>
                </li>
                <li className="w-100">
                  <a href="javascript:void(0)" className="d-flex text-primary text-decoration-none w-100 px-3 py-2 gap-2">
                    <DownloadInactiveIcon width={18} height={18} />
                    Download Report 1
                  </a>
                </li>
                <li className="w-100">
                  <a href="javascript:void(0)" className="d-flex text-primary text-decoration-none w-100 px-3 py-2 gap-2">
                    <UserInactiveIcon width={18} height={18} />
                    Users 1
                  </a>
                </li>
              </ul>
            </div>     
            <a
              href="#MasterRecSubMenu"
              className="text-primary text-decoration-none border-bottom border-primary sidebar-mainMenu"
              role="button"
              {...getDropdownAttributes('masterRecords').triggerAttributes}
            >
              Master Records
            </a>

            {/* Submenu with collapse */}
            <div {...getDropdownAttributes('masterRecords').contentAttributes}>
              <ul className="list-unstyled py-2 mb-0">
                <li className={`w-100 ${isActiveRoute('/AddUser') ? 'active' : ''}`}>
                  <Link to="/AddUser" className="d-flex text-primary text-decoration-none w-100 px-3 py-2 gap-2">
                    Add User
                  </Link>
                </li>
                <li className={`w-100 ${isActiveRoute('/AddNada') ? 'active' : ''}`}>
                  <Link to="/AddNada" className="d-flex text-primary text-decoration-none w-100 px-3 py-2 gap-2">
                    Add Nada
                  </Link>
                </li>
                <li className={`w-100 ${isActiveRoute('/AddVanchiName') ? 'active' : ''}`}>
                  <Link to="/AddVanchiName" className="d-flex text-primary text-decoration-none w-100 px-3 py-2 gap-2">
                    Add Vanchi Name
                  </Link>
                </li>
                <li className={`w-100 ${isActiveRoute('/AddVanchiNo') ? 'active' : ''}`}>
                  <Link to="/AddVanchiNo" className="d-flex text-primary text-decoration-none w-100 px-3 py-2 gap-2">
                    Add Vanchi No.
                  </Link>
                </li>                
              </ul>
            </div>                                    
          </div>
          <div className="mt-auto px-3 pt-2 small pb-4 sidebar-bottom-nav">
            <ul className="list-unstyled p-0 mb-0">
              <li><Link to="/about" className="text-decoration-none text-primary">About</Link></li>
              <li><Link to="/legal" className="text-decoration-none text-primary">Legal</Link></li>
              <li><Link to="/copyright" className="text-decoration-none text-primary">Copyright</Link></li>
              <li><Link to="/contact" className="text-decoration-none text-primary">Contact us</Link></li>
              <li>
                <p className="sidebar-powerBy mt-3 text-primary mb-0">Powered by</p>
                <ScooproIcon width={65} height={15} />
              </li>
            </ul>
          </div>
        </div>

        {/* Main Content */}
        <div
          className="flex-grow-1 main-content"
          style={{ marginLeft: 'var(--sidebar-width)' }}
        >
          {/* Page Content */}
          <div className="container-fluid p-4" style={{ height: 'calc(100% - (3rem - 45px))' }}>
            {children}
          </div>
        </div>
      </div>
      
      {/* Footer - Direct child of master layout */}
      <Footer />
    </div>
  );
};

export default MasterLayout;
